import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { SDLC_PATH } from '../../config';
import { countsLine, phaseReference } from '../../content';
import {
  cardSurfaceSx,
  flushStageEdgeCardSx,
  interactiveCardSx,
  pressedCardSx,
  stageColourFor,
} from '../../theme';
import type { PhaseCardProps } from './types';

/**
 * One phase in the L1 grid — the whole card is the link into it.
 *
 * **It stretches to fill its column, and the counts sit at the bottom.** The
 * grid gives every column the same height, so the flexible gap above the counts
 * is what puts them on a common baseline across a row — four cards whose
 * descriptions are different lengths still line up where it matters. A card
 * sized by its own content would give a ragged row of footers.
 *
 * **The description is clamped to three lines rather than truncated by the
 * API.** The full text belongs to the phase page; the card shows as much as
 * fits and no more, and how much fits is a layout question that changes with
 * the breakpoint.
 */
export const PhaseCard = ({ phase }: PhaseCardProps) => {
  const stageColour = stageColourFor(phase.stage, 'onSurface');
  const counts = countsLine(phase);

  return (
    <RouterLink
      to={`${SDLC_PATH}/${phase.path}`}
      style={{ textDecoration: 'none', display: 'flex', height: '100%' }}
    >
      <Box
        sx={{
          ...cardSurfaceSx,
          ...interactiveCardSx,
          ...flushStageEdgeCardSx,
          ...pressedCardSx(stageColour),
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          // The stage edge is flush to the card's top corners, so the card
          // clips rather than padding it in. A radius with a square bar under
          // it is the artefact this prevents.
          overflow: 'hidden',
        }}
      >
        <Box sx={{ height: 4, backgroundColor: stageColour, flexShrink: 0 }} />

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            p: 6,
          }}
        >
          <Typography
            variant="caption"
            component="span"
            sx={{ fontWeight: 500, letterSpacing: '1px', color: 'text.secondary' }}
          >
            {phaseReference(phase.number)}
          </Typography>

          <Typography variant="h3" component="h3" sx={{ color: 'text.primary' }}>
            {phase.name}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              overflow: 'hidden',
            }}
          >
            {phase.description}
          </Typography>

          {/* Takes the slack, so the counts land on the card's bottom edge
              however tall the column makes it. */}
          <Box sx={{ flex: 1 }} />

          {counts !== undefined && (
            <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
              {counts}
            </Typography>
          )}
        </Box>
      </Box>
    </RouterLink>
  );
};
